// src/components/student/DocumentHistory.jsx
import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import { Block, CloudOff, HourglassEmpty, Warning, CheckCircle, ErrorOutline, Search, Cancel, Visibility, CalendarToday, Description, HelpOutline } from '@mui/icons-material'; // Ensure all needed icons are imported
import { useUser } from '../../context/UserContext';
import { useDocuments } from '../../context/DocumentContext';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Tema personalizado
const theme = createTheme({
  palette: {
    primary: {
      main: '#B22222', // Color rojo sangre toro (Universidad del Valle)
    },
    secondary: {
      main: '#1976d2',
    },
    success: {
      main: '#4caf50',
      light: '#e8f5e9',
    },
    warning: {
      main: '#ff9800',
      light: '#fff3e0',
    },
    error: {
      main: '#f44336',
      light: '#ffebee',
    },
    info: {
      main: '#2196f3',
      light: '#e3f2fd',
    }
  },
});

// Componente para el indicador de estado (Ensure consistency with Dashboard)
const StatusChip = ({ status }) => {
  let icon, color, label;

  switch (status?.toLowerCase()) {
    case 'cumplido':
    case 'aprobado':
      icon = <CheckCircle />;
      color = 'success';
      label = 'Aprobado';
      break;
    case 'rechazado':
      icon = <ErrorOutline />; // Consistent icon
      color = 'error';
      label = 'Rechazado';
      break;
    case 'expirado':
    case 'vencido':
      icon = <Warning />;
      color = 'warning';
      label = 'Vencido';
      break;
    case 'sin revisar': // Map backend state
    case 'pendiente':
      icon = <HourglassEmpty />;
      color = 'info';
      label = 'Pendiente';
      break;
     case 'no aplica':
        icon = <Block />; // Example icon
        color = 'default';
        label = 'No Aplica';
        break;
    // No 'Sin cargar' state here as this view only shows uploaded docs
    default:
      icon = <HelpOutline />; // Icon for unknown status
      color = 'default';
      label = status || 'Desconocido';
  }

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      size="small"
      variant="outlined"
      sx={{ minWidth: '110px' }}
    />
  );
};

const DocumentHistory = () => {
  const { user } = useUser();
  const { userDocuments, documentTypes, loading } = useDocuments(); // Use context
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState(0);
  const [approvedDocs, setApprovedDocs] = useState(0);
  const [pendingDocs, setPendingDocs] = useState(0);
  const [rejectedDocs, setRejectedDocs] = useState(0);
  
  // Preparar los datos combinando documentTypes y userDocuments
  useEffect(() => {
    if (loading || !userDocuments || !documentTypes) {
        setFilteredHistory([]); // Clear history while loading or if no data
        return;
    };

    // Filter only documents that have been uploaded (have a carga date)
    const uploadedDocuments = userDocuments.filter(doc => doc.fecha_cargue);

    const historyItems = uploadedDocuments.map(userDoc => {
      const docType = documentTypes.find(type => type.id_doc === userDoc.id_doc) || {};

      // Map backend 'estado' to frontend display status
      let displayStatus = userDoc.estado || 'Pendiente'; // Default to Pendiente if null/empty
      if (displayStatus.toLowerCase() === 'cumplido') {
          displayStatus = 'Aprobado';
      } else if (displayStatus.toLowerCase() === 'sin revisar') {
          displayStatus = 'Pendiente';
      }

      return {
        id: userDoc.id_usuarioDoc,
        documentId: userDoc.id_doc,
        // Use nombre_doc from docType, ensure backend provides this field for types
        documentName: docType.nombre_doc || `Documento ID: ${userDoc.id_doc}`,
        uploadDate: userDoc.fecha_cargue,
        // Add other fields if needed, ensure backend provides them in getUserDocuments
        // expeditionDate: userDoc.fecha_expedicion,
        // expirationDate: userDoc.fecha_vencimiento,
        reviewDate: userDoc.fecha_revision,
        status: displayStatus, // Use the mapped status
        filePath: userDoc.ruta_archivo, // Ensure backend provides this
        comments: userDoc.comentarios, // Ensure backend provides this
        // expires: docType.vence === 'si' // Keep if needed
      };
    });

    // Sort by upload date (most recent first)
    historyItems.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));

    setFilteredHistory(historyItems); // Set the base list for filtering

    // Update counters (ensure status check matches mapped displayStatus)
    setUploadedDocs(historyItems.length);
    setApprovedDocs(historyItems.filter(doc => doc.status === 'Aprobado').length);
    setPendingDocs(historyItems.filter(doc => doc.status === 'Pendiente').length);
    setRejectedDocs(historyItems.filter(doc => doc.status === 'Rechazado').length);
    // Add counters for Vencido, No Aplica if needed

  }, [userDocuments, documentTypes, loading]); // Add loading dependency


  // Filtrar al buscar
  useEffect(() => {
       // This effect should filter the *already processed* historyItems stored in state
       // It should NOT re-fetch or re-process from userDocuments/documentTypes
       if (!searchTerm) {
           // If search is cleared, reset to the full processed list
           // Re-run the processing logic to get the full sorted list if necessary,
           // or better, store the full processed list separately and filter from it.
           // Let's assume `combinedDocuments` holds the full processed list.
           // For simplicity here, we'll re-filter the base `historyItems` generated above.

           // Re-process if searchTerm is empty to reset filters
            if (loading || !userDocuments || !documentTypes) return;
            const uploadedDocuments = userDocuments.filter(doc => doc.fecha_cargue);
            const historyItems = uploadedDocuments.map(userDoc => {
                 const docType = documentTypes.find(type => type.id_doc === userDoc.id_doc) || {};
                 let displayStatus = userDoc.estado || 'Pendiente';
                 if (displayStatus.toLowerCase() === 'cumplido') displayStatus = 'Aprobado';
                 else if (displayStatus.toLowerCase() === 'sin revisar') displayStatus = 'Pendiente';
                 return { /* ... mapping ... */ status: displayStatus, documentName: docType.nombre_doc || `Documento ID: ${userDoc.id_doc}`, /* ... other fields ... */ };
            });
            historyItems.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
            setFilteredHistory(historyItems);

       } else {
           // Filter the current full list (assuming filteredHistory holds the full list when searchTerm is empty)
           const filtered = filteredHistory.filter(item =>
             item.documentName.toLowerCase().includes(searchTerm.toLowerCase())
           );
           setFilteredHistory(filtered); // Update state with filtered results
       }
       setPage(0); // Reset page number on search
   }, [searchTerm, userDocuments, documentTypes, loading]); // Add dependencies


  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Formato de fecha
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Función para determinar el color de fondo (Ensure consistency with Dashboard)
  const getRowBackground = (status) => {
     switch (status?.toLowerCase()) {
      case 'aprobado': // Use frontend display status
        return theme.palette.success.light;
      case 'rechazado':
        return theme.palette.error.light;
      case 'vencido': // Use frontend display status
        return theme.palette.warning.light;
      case 'pendiente': // Use frontend display status
        return theme.palette.info.light;
       case 'no aplica':
        return theme.palette.default.light; // Or inherit
      default:
        return 'inherit';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ padding: 3, marginTop: 12 }}>
        <Typography variant="h5" gutterBottom>
          Historial de Documentos
        </Typography>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Aquí puede ver el historial de todos los documentos que ha cargado.
        </Typography>
        
        {/* Resumen de estadísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: theme.palette.grey.light }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Total Cargados
                </Typography>
                <Typography variant="h4" color="primary">
                  {uploadedDocs}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <Description fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Documentos en el sistema
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: theme.palette.success.light }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Aprobados
                </Typography>
                <Typography variant="h4" color="success.main">
                  {approvedDocs}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <CheckCircle fontSize="small" color="success" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Documentos aprobados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: theme.palette.info.light }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pendientes
                </Typography>
                <Typography variant="h4" color="info.main">
                  {pendingDocs}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <HourglassEmpty fontSize="small" color="info" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  En espera de revisión
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: theme.palette.error.light }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Rechazados
                </Typography>
                <Typography variant="h4" color="error.main">
                  {rejectedDocs}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <ErrorOutline fontSize="small" color="error" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Requieren corrección
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Divider sx={{ mb: 4 }} />
        
        {/* Barra de búsqueda */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar documento..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')} edge="end">
                    <Cancel />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
        
        {/* Tabla de historial */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="tabla de historial de documentos">
              <TableHead>
                <TableRow>
                  {/* Adjust headers as needed */}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Documento</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fecha Cargue</TableCell>
                  {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fecha Expedición</TableCell> */}
                  {/* <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fecha Vencimiento</TableCell> */}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fecha Revisión</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Comentarios</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Ver</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Use filteredHistory for rendering */}
                {filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center"> {/* Adjusted colSpan */}
                  <Typography variant="body1" sx={{ py: 2 }}>
                    {/* Add check for userDocuments before filtering */}
                    {(userDocuments && userDocuments.filter(doc => doc.fecha_cargue).length === 0) ? "No ha cargado ningún documento." : "No se encontraron documentos con ese nombre."}
                  </Typography>
                </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((historyItem) => (
                      <TableRow
                        hover
                        key={historyItem.id}
                        sx={{ backgroundColor: getRowBackground(historyItem.status) }}
                      >
                        <TableCell>{historyItem.documentName}</TableCell>
                        <TableCell>
                          <StatusChip status={historyItem.status} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarToday fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            {formatDate(historyItem.uploadDate)}
                          </Box>
                        </TableCell>
                        {/* <TableCell>{formatDate(historyItem.expeditionDate)}</TableCell> */}
                        {/* <TableCell>{historyItem.expires ? formatDate(historyItem.expirationDate) : '—'}</TableCell> */}
                        <TableCell>{formatDate(historyItem.reviewDate)}</TableCell>
                        <TableCell>
                          {/* Tooltip for comments */}
                          {historyItem.comments ? (
                            <Tooltip title={historyItem.comments}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 150,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'default' // Indicate hover is for tooltip
                                }}
                              >
                                {historyItem.comments}
                              </Typography>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {/* View link */}
                          {historyItem.filePath ? (
                            <Tooltip title="Ver documento">
                              <IconButton
                                color="primary"
                                component="a"
                                href={historyItem.filePath}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* ... TablePagination (using filteredHistory.length) ... */}
           <TablePagination
             rowsPerPageOptions={[10, 25, 50]}
             component="div"
             count={filteredHistory.length} // Count based on filtered results
             rowsPerPage={rowsPerPage}
             page={page}
             onPageChange={handleChangePage}
             onRowsPerPageChange={handleChangeRowsPerPage}
             labelRowsPerPage="Filas por página:"
             labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
           />
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default DocumentHistory;